import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetLoginReportQuery } from '../services/auth/authApi';
import Loading from '../components/Loading';

// Left Arrow Icon
const LeftArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
  </svg>
);

// Right Arrow Icon
const RightArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
  </svg>
);


const LoginReportPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  const [startDate, setStartDate] = useState(formatDateForAPI(oneMonthAgo));
  const [endDate, setEndDate] = useState(formatDateForAPI(today));
  
  // Fetch login report data using RTK Query
  const { data, isLoading, error } = useGetLoginReportQuery(
    { userId, page: currentPage, limit: itemsPerPage, startDate, endDate },
    { skip: !userId }
  );
  
  const loginReports = data?.data || [];
  const user = data?.user || {};
  const pagination = data?.pagination || {};
  const totalPages = pagination.totalPages || 0;

  const handlePageChange = function(page) {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  
  const handleSearch = function() {
    setCurrentPage(1);
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-6xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-gray-600 px-5 py-3 flex items-center justify-between">
              <span className="text-2xl text-white font-normal capitalize">
                Login Report
              </span>
              <button 
                onClick={function() { navigate(-1); }}
                className="bg-blue-500 text-white px-4 py-1 hover:bg-blue-600"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Date Filters */}
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-gray-700 text-sm font-medium">Start Date:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={function(e) { setStartDate(e.target.value); }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-gray-700 text-sm font-medium">End Date:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={function(e) { setEndDate(e.target.value); }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="bg-blue-500 text-white px-4 py-1 hover:bg-blue-600 text-sm rounded"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto w-full">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">
                  {error?.data?.message || 'Failed to load login report'}
                </div>
              ) : (
                <table className="w-full border-collapse text-sm border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border border-gray-300 text-left font-medium">User Name</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">City</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">REGION</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">ISP</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">IP-ADDRESS</th>
                      <th className="p-2 border border-gray-300 text-left font-medium">LOGIN DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginReports.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-gray-500 bg-gray-50">
                          No login reports found
                        </td>
                      </tr>
                    ) : (
                      loginReports.map(function(report, index) {
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="p-2 border border-gray-300">
                              {user.userId || '-'}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {report.city || '-'}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {report.region || '-'}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {report.isp || '-'}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {report.ipAddress || '-'}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {report.loginDate || '-'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-end p-3 mx-3">
                <ul className="flex items-center gap-1">
                  <li>
                    <button
                      onClick={function() { handlePageChange(currentPage - 1); }}
                      disabled={currentPage === 1}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <LeftArrowIcon />
                    </button>
                  </li>
                  {Array.from({ length: Math.min(totalPages, 5) }, function(_, i) {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <li key={pageNum}>
                        <button
                          onClick={function() { handlePageChange(pageNum); }}
                          className={
                            'px-3 py-1 rounded ' +
                            (currentPage === pageNum 
                              ? 'bg-blue-500 text-white' 
                              : 'border border-gray-300 hover:bg-gray-100')
                          }
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li>
                    <button
                      onClick={function() { handlePageChange(currentPage + 1); }}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <RightArrowIcon />
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginReportPage;